import { StateMachineAdapter } from './StateMachineAdapter.ts';
import { AnimationController } from './animations.ts';
import { QuestionAdapter } from './question-adapter.ts';

class SurveyApp {
    constructor() {
        this.engine = new StateMachineAdapter();
        this.animations = new AnimationController();
        this.currentQuestionElement = null;
        this.isTransitioning = false;
        this.focusedOptionIndex = -1;
        this.currentOptions = [];
        this.lastUserPosition = null;
        this.furthestMainQuestionIndex = 0; // Track maximum progress reached

        this.initializeElements();
        this.bindEvents();
    }

    initializeElements() {
        // Create main container structure
        document.body.innerHTML = `
            <header id="survey-header">
                <div id="progress-section">
                    <div id="progress-bar-container">
                        <div id="progress-bar">
                            <div id="progress-fill"></div>
                        </div>
                    </div>
                    <div id="progress-counter">1 / 10</div>
                </div>
            </header>

            <main id="survey-container">
                <button id="prev-btn" class="prev-btn" disabled>previous</button>
                <div id="question-container">
                    <div id="current-slide" class="slide"></div>
                    <div id="next-slide" class="slide hidden"></div>
                </div>
            </main>

            <footer id="survey-footer">
                <div id="navigation">
                    <div id="timer-circle" class="timer-circle hidden">
                        <svg width="40" height="40" viewBox="0 0 40 40">
                            <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="2"/>
                            <circle id="timer-progress" cx="20" cy="20" r="16" fill="none"
                                    stroke="var(--slide-accent-color, #B8860B)" stroke-width="3"
                                    stroke-linecap="round"
                                    transform="rotate(-90 20 20)"
                                    stroke-dasharray="100.53 100.53"
                                    stroke-dashoffset="0"/>
                        </svg>
                    </div>
                </div>
            </footer>
        `;

        /* AIDEV-TODO: Major layout restructuring needed for UX improvements
         *
         * 1. PREVIOUS BUTTON LAYOUT SHIFT PREVENTION:
         *    - ISSUE: button.style.display = 'none'/'flex' causes content jumping
         *    - SOLUTION: Implement space reservation using:
         *      a) CSS Grid with grid-template-areas for button-area
         *      b) Min-height reservation: min-height: 4rem on button container
         *      c) Use opacity: 0 + pointer-events: none instead of display: none
         *    - CHANGE LOCATION: updateNavigation() method below
         *
         * 2. TIMER CIRCLE REPOSITIONING:
         *    - ISSUE: Timer in footer feels disconnected from answers being timed
         *    - SOLUTION: Move timer-circle from footer to answer-container
         *    - HTML CHANGES: Add timer-circle as child of answer-container in createQuestionHTML()
         *    - CSS CHANGES: Remove fixed positioning, add margin-top spacing
         *    - JS CHANGES: Update startAutoAdvance() to target new timer location
         *
         * 3. ANSWER CONTAINER CENTERING:
         *    - ISSUE: Answers flow after question text, not centered in viewport
         *    - SOLUTION: Use CSS Grid on question-container with template-areas:
         *      grid-template-areas: "question" "answers" "timer"
         *      grid-template-rows: auto 1fr auto (centers answers in available space)
         *    - BENEFITS: Better visual hierarchy, more professional appearance
         *
         * IMPLEMENTATION PRIORITY:
         * 1. Layout shift (highest UX impact)
         * 2. Answer centering (visual improvement)
         * 3. Timer repositioning (contextual improvement)
         *
         * TESTING REQUIREMENTS:
         * - Verify no layout shifts on all screen sizes
         * - Test with all question types (scale/boolean/open)
         * - Validate mobile keyboard behavior still works
         * - Ensure timer animations work in new position
         */

        this.elements = {
            header: document.getElementById('survey-header'),
            container: document.getElementById('survey-container'),
            footer: document.getElementById('survey-footer'),
            progressSection: document.getElementById('progress-section'),
            progressBarContainer: document.getElementById('progress-bar-container'),
            progressBar: document.getElementById('progress-bar'),
            progressFill: document.getElementById('progress-fill'),
            progressCounter: document.getElementById('progress-counter'),
            currentSlide: document.getElementById('current-slide'),
            nextSlide: document.getElementById('next-slide'),
            prevBtn: document.getElementById('prev-btn'),
            timerCircle: document.getElementById('timer-circle'),
            timerProgress: document.getElementById('timer-progress')
        };
    }

    bindEvents() {
        this.elements.prevBtn.addEventListener('click', () => this.handlePrevious());

        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            if (this.isTransitioning) return;

            // Handle Enter key in input/textarea fields
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                if (e.key === 'Enter') {
                    if (e.target.tagName === 'TEXTAREA' && e.shiftKey) {
                        // Allow Shift+Enter for new lines in textarea
                        return;
                    } else if (e.target.tagName === 'INPUT' || (e.target.tagName === 'TEXTAREA' && !e.shiftKey)) {
                        // Submit on Enter for input, or Enter without Shift for textarea
                        e.preventDefault();

                        // Trigger change event to save the answer
                        const changeEvent = new Event('change', { bubbles: true });
                        e.target.dispatchEvent(changeEvent);

                        // Advance to next question if answer is valid
                        if (this.engine.validateCurrentAnswer()) {
                            this.handleNext();
                        }
                        return;
                    }
                }
                return; // Don't process other keys for input fields
            }

            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                // If an option is focused, select it; otherwise advance to next question
                if (this.focusedOptionIndex >= 0 && this.currentOptions.length > 0) {
                    const focusedOption = this.currentOptions[this.focusedOptionIndex];
                    focusedOption.click();
                } else {
                    this.handleNext();
                }
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                this.focusPreviousOption();
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                this.focusNextOption();
            } else if (e.key === 'ArrowLeft') {
                this.handlePrevious();
            } else if (e.key === 'ArrowRight') {
                this.handleNext();
            } else if (/^[1-9]$/.test(e.key)) {
                // Handle number keys 1-9 for scale questions
                const currentQuestion = this.engine.getCurrentQuestion();
                if (currentQuestion && currentQuestion.type === 'scale') {
                    const number = parseInt(e.key);
                    if (number >= currentQuestion.scale.min && number <= currentQuestion.scale.max) {
                        e.preventDefault();
                        this.selectAnswer(currentQuestion.id, number);
                    }
                }
            }
        });
    }

    async init() {
        try {
            await this.engine.loadSurvey();

            // Check for saved progress and auto-restore if available
            const savedProgress = this.engine.loadProgress();
            if (savedProgress) {
                // Auto-restore saved progress (passive resume)
                const restored = this.engine.restoreProgress(savedProgress);
                if (restored) {
                    console.log('Auto-restored from:', new Date(savedProgress.timestamp));
                }
            }

            this.initializeProgressBar();
            this.renderInitialQuestion();
            this.updateProgress();
            this.updateNavigation();
        } catch (error) {
            console.error('Failed to initialize survey:', error);
            this.showError('Failed to load survey. Please try again.');
        }
    }

    renderInitialQuestion() {
        const question = this.engine.getCurrentQuestion();
        if (!question) {
            this.showCompletion();
            return;
        }

        // Use adapter to convert Question to UI format
        const adaptedQuestion = QuestionAdapter.adapt(question);
        const slideContent = this.createQuestionHTML(adaptedQuestion);
        this.elements.currentSlide.innerHTML = slideContent;

        // Set accent color
        this.setAccentColor(adaptedQuestion);

        // Animate content in
        this.animations.animateContentIn(this.elements.currentSlide);

        // Initialize option navigation
        this.initializeOptionNavigation();

        // Focus management - ensure text inputs get focus for mobile keyboard
        this.focusFirstInput();
    }

    createQuestionHTML(question) {
        const accentColor = question.accent_color || '#B8860B';
        const accentColorDark = this.darkenColor(accentColor, 20);

        let html = `
            <div class="question-wrapper" data-level="${question.level}" style="--slide-accent-color: ${accentColor}; --slide-accent-color-dark: ${accentColorDark};">
                <div class="question-number" style="color: ${accentColor};">${this.getQuestionNumber(question)}</div>
                <h2 class="question-text">${question.question}</h2>
        `;

        if (question.description) {
            html += `<p class="question-description">${question.description}</p>`;
        }

        html += '<div class="answer-container">';
        html += this.createAnswerHTML(question);
        html += '</div></div>';

        return html;
    }

    getQuestionNumber(question) {
        // Simply return the pre-calculated display number!
        return question.displayNumber || "?)";
    }

    createAnswerHTML(question) {
        // Handle questions with maxSelections > 1 as multi-choice regardless of type
        if (question.maxSelections && question.maxSelections > 1) {
            return this.createMultiChoiceHTML(question);
        }
        
        switch (question.type) {
            case 'scale':
                return this.createScaleHTML(question);
            case 'boolean':
            case 'single-choice':
                return this.createSingleChoiceHTML(question);
            case 'multi-choice':
                return this.createMultiChoiceHTML(question);
            case 'open':
                return this.createOpenHTML(question);
            default:
                return '<p>Unknown question type</p>';
        }
    }

    createScaleHTML(question) {
        const currentAnswer = this.engine.answers[question.diagramId];
        let html = '<div class="scale-container">';

        /* AIDEV-TODO: CRITICAL - Restore previously selected answers when navigating back
         * ISSUE: When user goes Previous, their selected answer is not visually shown
         * CAUSE: Line below sets selected = '' instead of using currentAnswer
         * FIX: Replace with: const selected = (currentAnswer === i) ? 'selected' : '';
         * CONTEXT: User answers Q1, goes to Q2, clicks Previous - Q1 should show their choice
         * IMPACT: Users lose confidence in data persistence, creates UX confusion
         * IMPLEMENTATION: Use currentAnswer to conditionally add 'selected' CSS class
         */

        for (let i = question.scale.min; i <= question.scale.max; i++) {
            const label = question.scale.labels ? question.scale.labels[i] : i.toString();
            // Restore previously selected answer when navigating back
            const selected = (currentAnswer === i) ? 'selected' : '';

            html += `
                <button class="scale-option ${selected}"
                        data-value="${i}"
                        onclick="app.selectAnswer('${question.diagramId}', ${i})">
                    <span class="scale-number">${i}</span>
                    <span class="scale-label">${label}</span>
                </button>
            `;
        }

        html += '</div>';
        return html;
    }


    createMultiChoiceHTML(question) {
        const currentAnswer = this.engine.answers[question.diagramId];
        const selectedValues = Array.isArray(currentAnswer) ? currentAnswer : (currentAnswer ? [currentAnswer] : []);
        const maxSelections = question.maxSelections || 1;
        
        if (!question.options || !Array.isArray(question.options)) {
            return '<p>No options available</p>';
        }

        let html = '<div class="multi-choice-container">';
        
        // Add instruction text for multi-select
        if (maxSelections > 1) {
            html += `<div class="multi-choice-instruction">Pick ${maxSelections} (${selectedValues.length}/${maxSelections} selected)</div>`;
        }
        
        question.options.forEach(option => {
            const isSelected = selectedValues.includes(option.value);
            const escapedValue = option.value.replace(/'/g, "\\'");
            html += `
                <button class="multi-choice-option ${isSelected ? 'selected' : ''}"
                        data-value="${option.value}"
                        onclick="app.selectMultiAnswer('${question.diagramId}', '${escapedValue}', ${maxSelections})">
                    ${option.label}
                </button>
            `;
        });
        
        // Add submit button for multi-select questions
        if (maxSelections > 1) {
            const hasRequiredSelections = selectedValues.length === maxSelections;
            const submitDisabled = !hasRequiredSelections ? 'disabled' : '';
            html += `
                <button class="submit-btn ${submitDisabled}"
                        id="submit-${question.diagramId}"
                        onclick="app.submitMultiAnswer('${question.diagramId}')"
                        ${submitDisabled}>
                    Submit
                </button>
            `;
        }
        
        html += '</div>';
        return html;
    }

    createSingleChoiceHTML(question) {
        const currentAnswer = this.engine.answers[question.diagramId];
        
        if (!question.options || !Array.isArray(question.options)) {
            return '<p>No options available</p>';
        }

        let html = '<div class="boolean-container">';  // Use same container as boolean for consistent styling
        
        question.options.forEach(option => {
            if (option.isTextInput) {
                // Render as input field for [short text] options
                const inputId = `input_${question.diagramId}_${option.value.replace(/\W/g, '_')}`;
                const currentTextValue = currentAnswer === option.value ? '' : 
                    (typeof currentAnswer === 'object' && currentAnswer && currentAnswer.option === option.value) ? 
                    currentAnswer.text : '';
                
                html += `
                    <div class="text-input-option">
                        <label for="${inputId}">${option.label.replace(' [short text]', '')}</label>
                        <input type="text" 
                               id="${inputId}"
                               placeholder="Your answer..." 
                               value="${currentTextValue}"
                               onchange="app.selectTextAnswer('${question.diagramId}', '${option.value}', this.value)"
                               onkeyup="app.selectTextAnswer('${question.diagramId}', '${option.value}', this.value)">
                    </div>
                `;
            } else {
                // Regular button option
                const isSelected = currentAnswer === option.value;
                const escapedValue = option.value.replace(/'/g, "\\'");
                html += `
                    <button class="boolean-option ${isSelected ? 'selected' : ''}"
                            data-value="${option.value}"
                            onclick="app.selectAnswer('${question.diagramId}', '${escapedValue}')">
                        ${option.label}
                    </button>
                `;
            }
        });
        
        html += '</div>';
        return html;
    }

    createOpenHTML(question) {
        const currentAnswer = this.engine.answers[question.diagramId] || '';
        const isMultiline = question.multiline;
        const hasAnswer = currentAnswer.trim().length > 0;
        const isOptional = question.optional;

        let html = '<div class="open-question-container">';

        if (isMultiline) {
            html += `
                <textarea class="open-input"
                          id="input-${question.diagramId}"
                          placeholder="${question.placeholder || 'Type your answer here...'}"
                          onchange="app.selectAnswer('${question.diagramId}', this.value)"
                          oninput="app.selectAnswer('${question.diagramId}', this.value)"
                          onkeydown="app.handleOpenQuestionKeydown(event, '${question.diagramId}')"
                          rows="4">${currentAnswer}</textarea>
            `;
        } else {
            html += `
                <input type="text" class="open-input"
                       id="input-${question.diagramId}"
                       placeholder="${question.placeholder || 'Type your answer here...'}"
                       value="${currentAnswer}"
                       onchange="app.selectAnswer('${question.diagramId}', this.value)"
                       oninput="app.selectAnswer('${question.diagramId}', this.value)"
                       onkeydown="app.handleOpenQuestionKeydown(event, '${question.diagramId}')">
            `;
        }

        // Add hint text below the input
        html += '<div class="input-hint">Shift+Enter to add new line</div>';

        // Add submit button below the hint
        const submitDisabled = !isOptional && !hasAnswer ? 'disabled' : '';
        html += `
            <button class="submit-btn ${submitDisabled}"
                    id="submit-${question.diagramId}"
                    onclick="app.submitAnswer('${question.diagramId}')"
                    ${submitDisabled}>
                Submit
            </button>
        `;

        html += '</div>';
        return html;
    }

    selectAnswer(questionId, value) {
        console.log('🔍 selectAnswer called with:', { questionId, value });
        console.log('🔍 Current question:', this.engine.getCurrentQuestion()?.diagramId);
        
        this.engine.setAnswer(questionId, value);
        
        // Debug: Check if answer was stored
        console.log('🔍 Answer stored? Checking engine.answers:', this.engine.answers);
        console.log('🔍 Specific answer for', questionId, ':', this.engine.answers.get ? this.engine.answers.get(questionId) : this.engine.answers[questionId]);

        // Save progress to browser storage
        this.engine.saveProgress();

        // Update visual selection
        const container = document.querySelector('.answer-container');
        container.querySelectorAll('.selected').forEach(el => el.classList.remove('selected'));

        if (value !== null && value !== '') {
            // Find element by comparing data-value attribute directly to avoid CSS selector issues with special characters
            const elements = container.querySelectorAll('[data-value]');
            for (const element of elements) {
                if (element.dataset.value === value) {
                    element.classList.add('selected');
                    break;
                }
            }
        }

        this.updateNavigation();
        this.updateSubmitButtonState(questionId, value);

        // Auto-advance for boolean, scale, and single-choice questions (animation moved to timer circle)
        const question = this.engine.getCurrentQuestion();
        console.log('🔍 Question type for auto-advance:', question?.type);
        if (question && (question.type === 'boolean' || question.type === 'scale' || question.type === 'single-choice')) {
            console.log('🔍 Starting auto-advance...');
            this.startAutoAdvance();
        }
    }

    selectTextAnswer(questionId, optionValue, textValue) {
        console.log('🔍 selectTextAnswer called with:', { questionId, optionValue, textValue });
        
        // Store the text input as a structured answer
        const answer = {
            option: optionValue,
            text: textValue
        };
        
        this.engine.setAnswer(questionId, optionValue); // Store the option value for routing
        
        // Save progress to browser storage
        this.engine.saveProgress();
        
        // Update visual selection - highlight the text input container
        const container = document.querySelector('.answer-container');
        container.querySelectorAll('.text-input-option').forEach(el => {
            el.classList.remove('selected');
        });
        
        if (textValue.trim().length > 0) {
            // Find and highlight the current text input container
            const inputElement = document.getElementById(`input_${questionId}_${optionValue.replace(/\W/g, '_')}`);
            if (inputElement) {
                inputElement.closest('.text-input-option').classList.add('selected');
            }
        }
        
        this.updateNavigation();
        
        // Enable auto-advance if text has content
        if (textValue.trim().length > 0) {
            const question = this.engine.getCurrentQuestion();
            if (question && question.type === 'single-choice') {
                console.log('🔍 Starting auto-advance for text input...');
                this.startAutoAdvance();
            }
        }
    }

    selectMultiAnswer(questionId, optionValue, maxSelections) {
        console.log('🔍 selectMultiAnswer called with:', { questionId, optionValue, maxSelections });
        
        const currentAnswer = this.engine.answers[questionId];
        let selectedValues = Array.isArray(currentAnswer) ? [...currentAnswer] : (currentAnswer ? [currentAnswer] : []);
        
        // Toggle selection
        const index = selectedValues.indexOf(optionValue);
        if (index > -1) {
            // Deselect
            selectedValues.splice(index, 1);
        } else {
            // Select (if under limit)
            if (selectedValues.length < maxSelections) {
                selectedValues.push(optionValue);
            }
        }
        
        // Store the array of selected values
        this.engine.setAnswer(questionId, selectedValues);
        
        // Save progress
        this.engine.saveProgress();
        
        // Update visual state
        this.updateMultiChoiceVisualState(questionId, selectedValues, maxSelections);
        this.updateNavigation();
    }

    updateMultiChoiceVisualState(questionId, selectedValues, maxSelections) {
        const container = document.querySelector('.answer-container');
        
        // Update button states
        container.querySelectorAll('.multi-choice-option').forEach(button => {
            const value = button.dataset.value;
            if (selectedValues.includes(value)) {
                button.classList.add('selected');
            } else {
                button.classList.remove('selected');
            }
        });
        
        // Update instruction text
        const instruction = container.querySelector('.multi-choice-instruction');
        if (instruction) {
            instruction.textContent = `Pick ${maxSelections} (${selectedValues.length}/${maxSelections} selected)`;
        }
        
        // Update submit button
        const submitBtn = document.getElementById(`submit-${questionId}`);
        if (submitBtn) {
            if (selectedValues.length === maxSelections) {
                submitBtn.disabled = false;
                submitBtn.classList.remove('disabled');
            } else {
                submitBtn.disabled = true;
                submitBtn.classList.add('disabled');
            }
        }
    }

    submitMultiAnswer(questionId) {
        if (this.isTransitioning) return;
        
        const question = this.engine.getCurrentQuestion();
        if (!question || question.diagramId !== questionId) return;
        
        const selectedValues = this.engine.answers[questionId];
        const maxSelections = question.maxSelections || 1;
        
        // Validate selection count
        if (!Array.isArray(selectedValues) || selectedValues.length !== maxSelections) {
            this.showValidationError();
            return;
        }
        
        // For multi-select questions, we need to determine next question based on the first selected option
        // This is a limitation of the current routing system - it can only handle single values
        const firstSelection = selectedValues[0];
        this.engine.setAnswer(questionId, firstSelection); // Temporarily set single value for routing
        
        // Proceed to next question
        this.handleNext();
    }

    handleOpenQuestionKeydown(event, questionId) {
        // Handle Enter key behavior in open questions to prevent navigation issues
        if (event.key === 'Enter') {
            // For textarea (multiline), allow Shift+Enter for new lines
            if (event.target.tagName.toLowerCase() === 'textarea') {
                if (event.shiftKey) {
                    // Allow Shift+Enter to create new line
                    return;
                } else {
                    // Plain Enter in textarea - prevent default and focus submit button
                    event.preventDefault();
                    const submitBtn = document.getElementById(`submit-${questionId}`);
                    if (submitBtn && !submitBtn.disabled) {
                        submitBtn.focus();
                    }
                    return;
                }
            }
            
            // For single-line input, prevent Enter from causing issues
            if (event.target.tagName.toLowerCase() === 'input') {
                event.preventDefault();
                // Focus the submit button if it exists and is enabled
                const submitBtn = document.getElementById(`submit-${questionId}`);
                if (submitBtn && !submitBtn.disabled) {
                    submitBtn.focus();
                }
            }
        }
    }

    updateSubmitButtonState(questionId, value) {
        const submitBtn = document.getElementById(`submit-${questionId}`);
        if (!submitBtn) return;

        const question = this.engine.getCurrentQuestion();
        if (!question) return;

        const hasAnswer = value && value.trim().length > 0;
        const isOptional = question.optional;

        if (isOptional || hasAnswer) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('disabled');
        } else {
            submitBtn.disabled = true;
            submitBtn.classList.add('disabled');
        }
    }

    submitAnswer(questionId) {
        if (this.isTransitioning) return;

        const question = this.engine.getCurrentQuestion();
        if (!question || question.id !== questionId) return;

        const answer = this.engine.answers[questionId];
        const hasAnswer = answer && answer.trim && answer.trim().length > 0;
        const isOptional = question.optional;

        // Check if submission is valid
        if (!isOptional && !hasAnswer) {
            this.showValidationError();
            return;
        }

        // Proceed to next question (same as Next button functionality)
        this.handleNext();
    }

    async handleNext() {
        console.log('🔍 handleNext called');
        if (this.isTransitioning) return;

        const isValid = this.engine.validateCurrentAnswer();
        console.log('🔍 validateCurrentAnswer returned:', isValid);
        console.log('🔍 Current question:', this.engine.getCurrentQuestion()?.diagramId);
        console.log('🔍 All answers in engine:', this.engine.answers);
        
        if (!isValid) {
            console.log('🔍 Validation failed, showing error');
            this.showValidationError();
            return;
        }

        this.isTransitioning = true;
        this.stopAutoAdvance();

        // Get next question first
        const nextQuestion = this.engine.nextQuestion();
        console.log('🔍 nextQuestion() returned:', nextQuestion);
        console.log('🔍 nextQuestion diagramId:', nextQuestion?.diagramId);
        if (nextQuestion) {
            // Use adapter to convert Question to UI format
            const adaptedQuestion = QuestionAdapter.adapt(nextQuestion);
            
            // Set new accent color
            this.setAccentColor(adaptedQuestion);

            // Prepare next slide content
            const nextSlideContent = this.createQuestionHTML(adaptedQuestion);

            // Track furthest progress reached (never goes backward)
            const currentMainIndex = this.getCurrentMainQuestionIndex();
            this.furthestMainQuestionIndex = Math.max(this.furthestMainQuestionIndex, currentMainIndex);

            // Update progress immediately for snappy response
            this.updateProgress();

            // Animate transition and render new content
            await this.animations.slideTransition('next', nextSlideContent);
            this.updateNavigation();
            this.initializeOptionNavigation();
            this.focusFirstInput();

            // Save progress after successful navigation
            this.engine.saveProgress();
        } else {
            console.log('🔍 No next question found, showing completion screen');
            this.showCompletion();
        }

        this.isTransitioning = false;
    }

    async handlePrevious() {
        if (this.isTransitioning) return;

        this.isTransitioning = true;
        this.stopAutoAdvance();

        // Get previous question first
        const prevQuestion = this.engine.previousQuestion();
        if (prevQuestion) {
            // Use adapter to convert Question to UI format
            const adaptedQuestion = QuestionAdapter.adapt(prevQuestion);
            
            // Set new accent color
            this.setAccentColor(adaptedQuestion);

            // Prepare previous slide content
            const prevSlideContent = this.createQuestionHTML(adaptedQuestion);

            // Update progress immediately for snappy response (same as Next button)
            this.updateProgress();

            // Animate transition and render new content
            await this.animations.slideTransition('previous', prevSlideContent);
            this.updateNavigation();
            this.initializeOptionNavigation();
            this.focusFirstInput();
        }

        this.isTransitioning = false;
    }

    initializeProgressBar() {
        // Set a reasonable estimate for total questions per path
        // Since we're using DIAGRAM.md structure, each path has roughly 10-15 main questions
        this.totalMainQuestions = 15;

        // Initialize progress bar
        this.updateProgressBar();
    }

    updateProgressBar() {

        // Find which main question we're currently on
        const currentMainIndex = this.getCurrentMainQuestionIndex();

        /* AIDEV-TODO: Fix progress bar regression when clicking Previous button
         * ISSUE 1: Progress shows "1 / 5" and 20% fill when user hasn't answered first question yet
         * ISSUE 2: CRITICAL - Progress bar regresses when clicking Previous (e.g., from 2/5 back to 1/5)
         *
         * EXPECTED: Progress should track FURTHEST point reached, not current position
         * Example: User answers Q1 → Q2 (shows 2/5) → Previous to Q1 → should STILL show 2/5
         *
         * SOLUTION: Track furthest progress separately from current question:
         *   1. Add furthestMainQuestionIndex property to track maximum progress
         *   2. Update furthest only when advancing forward, never when going back
         *   3. Progress calculation: (furthestMainQuestionIndex + 1) / total
         *   4. Counter shows: "${furthestMainQuestionIndex + 1} / ${total}"
         *
         * IMPLEMENTATION:
         *   - Initialize furthestMainQuestionIndex = 0 in constructor
         *   - In handleNext(): furthestMainQuestionIndex = Math.max(furthestMainQuestionIndex, currentMainIndex + 1)
         *   - In handlePrevious(): Don't touch furthestMainQuestionIndex
         *   - Use furthestMainQuestionIndex for progress display, currentMainIndex for navigation
         *
         * CONTEXT: Progress bars represent accomplishment/completion, not current location
         * Users expect progress to never go backwards - it represents work done, not position
         */

        // Calculate progress percentage using furthest progress reached (never regresses)
        const progressPercentage = ((this.furthestMainQuestionIndex + 1) / this.totalMainQuestions) * 100;

        // Update progress bar fill
        this.elements.progressFill.style.width = `${progressPercentage}%`;

        // Update progress counter to show furthest progress
        const furthestQuestionNumber = this.furthestMainQuestionIndex + 1;
        this.elements.progressCounter.textContent = `${furthestQuestionNumber} / ${this.totalMainQuestions}`;

        // Set accent color for progress bar if current question has one
        const currentQuestion = this.engine.getCurrentQuestion();
        if (currentQuestion?.accent_color) {
            this.elements.progressFill.style.backgroundColor = currentQuestion.accent_color;
        }
    }

    async navigateToQuestion(questionId) {
        // Progress bar doesn't support navigation - simplified for now
        return;
    }


    getCurrentMainQuestionIndex() {
        const currentQuestion = this.engine.getCurrentQuestion();
        if (!currentQuestion) return 0;

        // Use pre-calculated question number
        return (currentQuestion.questionNumber || 1) - 1; // Convert to 0-based index
    }

    updateProgress() {
        this.updateProgressBar();
    }

    updateNavigation() {
        // Check if we're at the first question by checking if there's previous question available
        const isFirstQuestion = this.engine.getQuestionHistory().length === 0;
        this.elements.prevBtn.disabled = isFirstQuestion;

        // AIDEV-TODO: CRITICAL - Replace display toggle to prevent layout shift
        // CURRENT PROBLEM: this.elements.prevBtn.style.display causes content jumping
        // REPLACE THIS LINE with one of these CLS-prevention approaches:
        //
        // OPTION 1 (Recommended): Opacity + pointer-events
        // this.elements.prevBtn.style.opacity = isFirstQuestion ? '0' : '1';
        // this.elements.prevBtn.style.pointerEvents = isFirstQuestion ? 'none' : 'auto';
        //
        // OPTION 2: CSS Grid (requires CSS changes to reserve button area)
        // - Add grid-template-areas to #survey-container in CSS
        // - Use grid-area: button-area on .prev-btn
        // - Element stays in DOM, just invisible
        //
        // OPTION 3: Min-height reservation (simple but less flexible)
        // - Set min-height: 4rem on button container in CSS
        // - Remove this display toggling completely
        //
        // WHY: CLS (Cumulative Layout Shift) is Core Web Vital. Users experience
        // jarring visual jump when button appears/disappears. Reserved space prevents this.
        this.elements.prevBtn.style.display = isFirstQuestion ? 'none' : 'flex';

        // Next button removed - navigation handled by submit buttons on text inputs
    }

    showCompletion() {
        const surveyData = this.engine.getSurveyData();
        const completion = surveyData.completion;

        // Clear saved progress since survey is completed
        this.engine.clearProgress();

        let completionHTML = `
            <div class="completion-screen">
                <h1>${completion.title}</h1>
                <p class="completion-message">${completion.message}</p>
        `;

        // Add statistics if enabled
        if (completion.showStats && completion.stats) {
            completionHTML += `
                <div class="stats-section">
                    <h2 class="stats-title">📊 Survey Insights</h2>
                    <div class="stats-grid">
            `;

            completion.stats.forEach(stat => {
                completionHTML += `
                    <div class="stat-card">
                        <div class="stat-value">${stat.value}</div>
                        <div class="stat-label">${stat.label}</div>
                        <div class="stat-description">${stat.description}</div>
                    </div>
                `;
            });

            completionHTML += `
                    </div>
                </div>
            `;
        }

        // Add newsletter signup if enabled
        if (completion.newsletter && completion.newsletter.enabled) {
            const newsletter = completion.newsletter;
            completionHTML += `
                <div class="newsletter-section">
                    <h3 class="newsletter-title">${newsletter.title}</h3>
                    <p class="newsletter-description">${newsletter.description}</p>
                    <div class="newsletter-form">
                        <input type="email" class="newsletter-input" placeholder="${newsletter.placeholder}" id="newsletter-email">
                        <button class="newsletter-btn" onclick="app.subscribeNewsletter()">${newsletter.button}</button>
                    </div>
                </div>
            `;
        }

        completionHTML += `
                <button class="completion-btn" onclick="app.reset()">${completion.button}</button>
            </div>
        `;

        this.elements.currentSlide.innerHTML = completionHTML;

        // Add animation to stats
        if (completion.showStats) {
            requestAnimationFrame(() => {
                const statCards = this.elements.currentSlide.querySelectorAll('.stat-card');
                statCards.forEach((card, index) => {
                    setTimeout(() => {
                        card.classList.add('animate-in');
                    }, index * 150);
                });
            });
        }

        this.elements.prevBtn.style.display = 'none';
    }

    showValidationError() {
        // Show a subtle validation message
        const existing = document.querySelector('.validation-error');
        if (existing) existing.remove();

        const error = document.createElement('div');
        error.className = 'validation-error';
        error.textContent = 'Please answer this question before continuing.';

        this.elements.currentSlide.appendChild(error);

        setTimeout(() => error.remove(), 3000);
    }

    showError(message) {
        this.elements.currentSlide.innerHTML = `
            <div class="error-screen">
                <h2>Oops!</h2>
                <p>${message}</p>
                <button onclick="location.reload()">Try Again</button>
            </div>
        `;
    }

    focusFirstInput() {
        // Only focus text inputs for mobile keyboard - no pre-selection for buttons
        const textInput = this.elements.currentSlide.querySelector('input[type="text"], textarea');

        if (textInput) {
            // Increased delay for better compatibility with slide transitions and mobile browsers
            setTimeout(() => {
                textInput.focus();

                // Move cursor to end and ensure mobile keyboard shows up
                const length = textInput.value.length;
                textInput.setSelectionRange(length, length);

                // On mobile, this helps ensure keyboard shows up
                textInput.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }, 300); // Longer delay for mobile compatibility
        }
    }

    setAccentColor(question) {
        const accentColor = question.accent_color || '#B8860B';
        const accentColorDark = this.darkenColor(accentColor, 20);

        // Next button removed - accent colors handled by CSS variables on container
    }

    darkenColor(color, percent) {
        const num = parseInt(color.replace("#", ""), 16);
        const amt = Math.round(2.55 * percent);
        const R = (num >> 16) - amt;
        const G = (num >> 8 & 0x00FF) - amt;
        const B = (num & 0x0000FF) - amt;
        return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
    }

    startAutoAdvance() {
        console.log('🔍 startAutoAdvance called');
        const duration = 600; // 3 seconds as requested

        // Show and animate timer circle
        this.elements.timerCircle.classList.remove('hidden');
        this.elements.timerCircle.classList.add('active');

        // Set CSS custom property for animation duration
        this.elements.timerProgress.style.setProperty('--timer-duration', duration + 'ms');

        // Start counter-clockwise countdown animation
        requestAnimationFrame(() => {
            this.elements.timerProgress.classList.add('countdown');
        });

        // Start auto-advance timer
        this.autoAdvanceTimeout = setTimeout(() => {
            console.log('🔍 Auto-advance timer expired, calling handleNext()');
            this.handleNext();
        }, duration);
    }

    stopAutoAdvance() {
        if (this.autoAdvanceTimeout) {
            clearTimeout(this.autoAdvanceTimeout);
            this.autoAdvanceTimeout = null;
        }

        // Hide and reset timer circle
        this.elements.timerCircle.classList.add('hidden');
        this.elements.timerCircle.classList.remove('active');
        this.elements.timerProgress.classList.remove('countdown');

        // Reset animation
        this.elements.timerProgress.style.strokeDashoffset = '0';
    }

    initializeOptionNavigation() {
        const currentQuestion = this.engine.getCurrentQuestion();
        if (!currentQuestion) return;

        // Find all interactive options for the current question
        this.currentOptions = [];

        if (currentQuestion.type === 'scale') {
            this.currentOptions = Array.from(this.elements.currentSlide.querySelectorAll('.scale-option'));
        } else if (currentQuestion.type === 'boolean') {
            this.currentOptions = Array.from(this.elements.currentSlide.querySelectorAll('.boolean-option'));
        }

        // Reset focus
        this.focusedOptionIndex = -1;
        this.updateOptionFocus();
    }

    focusNextOption() {
        if (this.currentOptions.length === 0) return;

        this.focusedOptionIndex = Math.min(this.focusedOptionIndex + 1, this.currentOptions.length - 1);
        this.updateOptionFocus();
    }

    focusPreviousOption() {
        if (this.currentOptions.length === 0) return;

        this.focusedOptionIndex = Math.max(this.focusedOptionIndex - 1, -1);
        this.updateOptionFocus();
    }

    updateOptionFocus() {
        // Remove focus from all options
        this.currentOptions.forEach(option => {
            option.classList.remove('keyboard-focused');
        });

        // Add focus to current option
        if (this.focusedOptionIndex >= 0 && this.focusedOptionIndex < this.currentOptions.length) {
            this.currentOptions[this.focusedOptionIndex].classList.add('keyboard-focused');
        }
    }






    getTimeAgo(date) {
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }

    subscribeNewsletter() {
        const emailInput = document.getElementById('newsletter-email');
        const email = emailInput?.value?.trim();

        if (!email) {
            alert('Please enter your email address');
            return;
        }

        if (!this.isValidEmail(email)) {
            alert('Please enter a valid email address');
            return;
        }

        // Simulate newsletter subscription (replace with real API call)
        console.log('Newsletter subscription:', email);

        // Show success message
        const newsletterSection = document.querySelector('.newsletter-section');
        if (newsletterSection) {
            newsletterSection.innerHTML = `
                <div class="newsletter-success">
                    <div class="success-icon">✅</div>
                    <h3>Thanks for subscribing!</h3>
                    <p>We'll keep you updated on our product improvements.</p>
                </div>
            `;
        }
    }

    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    reset() {
        this.engine.reset();
        this.engine.clearProgress(); // Clear any saved progress
        this.lastUserPosition = null; // Clear last position
        this.elements.prevBtn.style.display = 'block';
        this.stopAutoAdvance();
        this.renderInitialQuestion();
        this.updateProgress();
        this.updateNavigation();
    }
}

// Initialize the app
window.app = new SurveyApp();
document.addEventListener('DOMContentLoaded', () => {
    window.app.init();
});
